import { zodResolver } from '@hookform/resolvers/zod';
import { type SolarRoofEdgeAnchor } from '@payaca/helpers/solarRoofEdgeAnchor';
import {
  Badge,
  Button,
  ButtonDropdown,
  ButtonRadioGroup,
  Divider,
  Field,
  Form,
  H3,
  H4,
  IncrementableNumberInput,
  InputGroup,
  NumberInput,
  Switch,
  SwitchSizeVariant,
  Text,
} from '@payaca/ambrosia';
import {
  ROOF_TEMPLATE_CYCLE,
  ROOF_TEMPLATE_LABELS,
  type RoofDrawKind,
} from '@payaca/components/solarRoofView/SolarRoofView';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, ReactNode, useState, useEffect, useRef } from 'react';
import { compassLabel, roofBearingLabel } from './roofLabels';
import { type InspectorFocusRequest } from './InspectorNavigation';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v4';
import {
  physicalRoofPlane,
  resolveSolarMounting,
  type SolarMounting,
  type SolarRoofPlane,
} from '@payaca/helpers/solarMounting';
import { hasMeasuredPlane } from '@payaca/helpers/solarPlaneFit';
import {
  formatRoofArea,
  formatRoofPitch,
  roofSurfaceAreaMeters2,
} from '@payaca/helpers/solarDesignerUnits';
import usePreferredNumberFormat from '@/hooks/usePreferredNumberFormat';
import { useAccount } from '@/api/rest/users/useAccount';
import { resolveSolarSavingsRates } from '../solarSavings';
import SunAccessHeatmap from './SunAccessHeatmap';
import {
  type FaceSummary,
  DesignerTool,
  EditPanel,
  WorkingFace,
} from './types';
import { type ConfirmRepositionOutcome } from './useSolarDesignerLayout';
import PlacementRulesFields from '@/ui/components/companySettings/PlacementRulesFields';
import DesignerHelp from './DesignerHelp';
import { Modal } from '@payaca/ambrosia';
import { useInspectorDialog } from './useInspectorDialog';
import {
  packSkipReason,
  parseSolarPlacementRules,
  type SolarPlacementRules,
} from '@payaca/helpers/solarPlacementRules';

type SolarSavingsView = {
  annualKwh: number | null;
  annualSavingsGbp: number | null;
  paybackYears: number | null;
};

export type ContextPanelProps = {
  focusRequest?: InspectorFocusRequest | null;
  onFocusHandled?: () => void;
  tool: DesignerTool;
  savingsRegion: 'uk' | 'us';
  carbonOffsetFactorKgPerMwh: number | null;
  netMeteringAllowed: boolean | null;
  drawTemplate: RoofDrawKind;
  setDrawTemplate: (kind: RoofDrawKind) => void;
  onSetSelectedFacePitch: (pitchDegrees: number) => void;
  onSetSelectedFaceMounting?: (mounting: SolarMounting) => void;
  onSetSelectedFaceAnchor?: (anchor: SolarRoofEdgeAnchor | null) => void;
  onConfirmSelectedRoofPlane?: (roofPlane: SolarRoofPlane) => void;
  selectedFace: WorkingFace | undefined;
  selectedFaceExcluded?: boolean;
  onToggleSelectedFaceIncluded?: () => void;
  selectedObstacleIndex: number | null;
  workingPanels: EditPanel[];
  faceSummaries: FaceSummary[];
  onSelectFace: (faceId: number) => void;
  /** Keeps the best N panels on a face by yield. */
  onSetFacePanelCount: (faceId: number, count: number) => void;
  /**
   * "Include anyway" on a roof that Array rules skip: overrides the rules for
   * that roof and repacks it. Without it the roof just reads "Excluded by
   * Array rules".
   */
  onIncludeSelectedFaceAnyway?: () => void;
  /** Clears "Include anyway": the roof follows the Array rules again. */
  onFollowRulesSelectedFace?: () => void;
  /**
   * The roof model's pitch and bearing for the selected roof (before any
   * override), for "Use computed"; null for a drawn roof.
   */
  computedRoofPlane?: SolarRoofPlane | null;
  workingObstacles: number;
  savings: SolarSavingsView;
  layoutDirty: boolean;
  geometryEditedBadge: boolean;
  roofModelLabel?: string | null;
  imageryQualityLabel?: { label: string; colour: 'green' | 'gray' } | null;
  imageryMonth?: string | null;
  isArrayRotating: boolean;
  onDeleteSelectedFace: () => void;
  clearSelection: () => void;
  onDeleteSelectedObstacle: () => void;
  isGeneratingGeometry: boolean;
  /** An unmoved pin requires a second, explicit confirmation to reanalyse. */
  confirmReposition: (options?: {
    force?: boolean;
  }) => ConfirmRepositionOutcome | void;
  cancelTool: () => void;
  parcelMismatchHint: ReactNode;
  onResetToAuto: () => void;
  placementRules?: SolarPlacementRules;
  onPlacementRulesChange?: (rules: SolarPlacementRules) => void;
  /**
   * Called after an Array rules, pitch, bearing or mounting change so the
   * designer can repack without a "pending · Repack" step (Undo covers
   * regret). `all` follows an Array rules change; `selected` a pitch,
   * bearing, mounting or panel-tilt override on the selected roof. Fired once per edit, before the parent's state settles:
   * repack from the settled state and coalesce bursts of numeric typing.
   */
  onAutoRepack?: (scope: 'all' | 'selected') => void;
  /** Snaps dragged panels to nearby panels on the same roof. */
  snapToArray?: boolean;
  onSnapToArrayChange?: (enabled: boolean) => void;
};

const ToolHint: FC<{
  title: string;
  children: ReactNode;
  onCancel: () => void;
  cancelLabel?: string;
}> = (props) => {
  const { title, children, onCancel, cancelLabel = 'Done' } = props;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <H4>{title}</H4>
        <Button variant="quaternary" size="sm" onClick={onCancel}>
          {cancelLabel}
        </Button>
      </div>
      <Text level="supporting">{children}</Text>
    </div>
  );
};

const FaceList: FC<{
  summaries: FaceSummary[];
  onSelectFace: (faceId: number) => void;
  savingsRegion: 'uk' | 'us';
}> = (props) => {
  const { summaries, onSelectFace, savingsRegion } = props;
  const numberFormat = usePreferredNumberFormat({ maximumFractionDigits: 0 });
  return (
    <ul
      aria-label="Roof faces"
      className="m-0 list-none divide-y divide-gray-100 p-0"
    >
      {summaries.map((face, index) => (
        <li key={face.faceId}>
          <Button
            variant="quaternary"
            onClick={() => onSelectFace(face.faceId)}
            // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Mixed grid template has no utility
            className="grid w-full grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 rounded-md px-2 py-2 text-left"
          >
            <Text as="span">
              Roof {index + 1} · {roofBearingLabel(face)}
              {face.rulesOverride && (
                <span className="ml-2 rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">
                  Rules overridden
                </span>
              )}
              {face.planeFit?.status === 'review' && (
                <Badge
                  variant="soft"
                  colour="yellow"
                  size="sm"
                  className="ml-2"
                >
                  Angles need review
                </Badge>
              )}
            </Text>
            <div className="text-right">
              <Text as="span">
                {face.panelsIncluded} panel
                {face.panelsIncluded === 1 ? '' : 's'}
              </Text>
            </div>
            <Text as="span" level="supporting">
              {hasMeasuredPlane(face)
                ? formatRoofPitch(face.pitchDegrees)
                : 'Unknown'}{' '}
              pitch · {formatRoofArea(face.areaMeters2, savingsRegion)}
            </Text>
            {face.annualKwh != null && (
              <div className="text-right">
                <Text as="span" level="supporting">
                  {numberFormat.format(face.annualKwh)} kWh/yr
                </Text>
              </div>
            )}
            {face.photoAgreement && face.photoAgreement.agrees !== 'yes' && (
              <div className="col-span-2 flex items-start gap-1.5">
                <UntitledIcon
                  name="alert-triangle.3"
                  className="mt-0.5 size-4 shrink-0 text-yellow-600"
                />
                <Text as="span" level="supporting">
                  {face.photoAgreement.agrees === 'no'
                    ? 'Roof outline needs checking on site'
                    : 'Tree cover or gaps: check on site'}
                </Text>
              </div>
            )}
          </Button>
        </li>
      ))}
    </ul>
  );
};

const DrawPanel: FC<
  Pick<ContextPanelProps, 'drawTemplate' | 'setDrawTemplate' | 'cancelTool'>
> = (props) => {
  const { drawTemplate, setDrawTemplate, cancelTool } = props;
  const methods = useForm({ values: { shape: drawTemplate } });
  return (
    <div className="space-y-4">
      <ToolHint title="Draw roof" onCancel={cancelTool} cancelLabel="Cancel">
        {drawTemplate === 'outline'
          ? 'Click each corner, then the first corner to close.'
          : 'Click two opposite corners.'}
      </ToolHint>
      <div className="space-y-1">
        <div className="flex items-center justify-between gap-2">
          <Text as="span">Roof shape</Text>
          <DesignerHelp title="About roof shapes">
            Q changes the shape · Esc cancels
          </DesignerHelp>
        </div>
        {/* Every shape stays visible, as on the production designer: a closed
            list hid the choice the user needs before their first click. */}
        <Form methods={methods} onSubmit={() => undefined}>
          <Field name="shape">
            <ButtonRadioGroup
              aria-label="Roof shape"
              sizeVariant="sm"
              value={drawTemplate}
              onChange={(kind: RoofDrawKind) => setDrawTemplate(kind)}
              options={ROOF_TEMPLATE_CYCLE.map((kind) => ({
                label: ROOF_TEMPLATE_LABELS[kind],
                value: kind,
              }))}
            />
          </Field>
        </Form>
      </div>
    </div>
  );
};

const FACE_FORM_SCHEMA = z.object({
  mounting: z.enum(['auto', 'flush', 'tilt']),
  panelTilt: z.number().min(0).max(45).nullable(),
  roofBearing: z.number().min(0).max(360).nullable(),
  pitch: z.number().min(0).max(75).nullable(),
  anchorOffset: z.number().min(0).max(100).nullable(),
});

type FaceFormValues = z.infer<typeof FACE_FORM_SCHEMA>;

type Fact = { label: string; value: ReactNode };

/** Read-only facts as compact label / value rows, one fact per row. */
const FactRows: FC<{
  label: string;
  facts: (Fact | false | null | undefined)[];
}> = (props) => (
  <dl aria-label={props.label} className="m-0 divide-y divide-gray-100">
    {props.facts
      .filter((fact): fact is Fact => !!fact)
      .map((fact) => (
        <div
          key={fact.label}
          className="flex items-baseline justify-between gap-3 py-1.5"
        >
          <dt className="shrink-0">
            <Text as="span" level="supporting">
              {fact.label}
            </Text>
          </dt>
          <dd className="m-0 min-w-0 text-right">
            <Text as="span">{fact.value}</Text>
          </dd>
        </div>
      ))}
  </dl>
);

/** "South-east" → "SE". */
const compassShort = (azimuthDegrees: number) =>
  compassLabel(azimuthDegrees)
    .split('-')
    .map((word) => word[0].toUpperCase())
    .join('');

/**
 * Which end of the roof slope an outline edge sits on. The roof plane's
 * bearing is its downslope direction, so an edge whose midpoint lies downslope
 * of the outline's centre is the lower edge (eave); otherwise it is the ridge.
 */
const inferRoofEdgeRole = (
  ring: { lat: number; lng: number }[],
  edgeIndex: number,
  downslopeAzimuthDegrees: number
): SolarRoofEdgeAnchor['role'] => {
  if (ring.length < 2) return 'eave';
  const lat0 = ring.reduce((sum, point) => sum + point.lat, 0) / ring.length;
  const lng0 = ring.reduce((sum, point) => sum + point.lng, 0) / ring.length;
  const cosLat = Math.cos((lat0 * Math.PI) / 180);
  const azimuth = (downslopeAzimuthDegrees * Math.PI) / 180;
  const start = ring[edgeIndex];
  const end = ring[(edgeIndex + 1) % ring.length];
  const east = ((start.lng + end.lng) / 2 - lng0) * cosLat;
  const north = (start.lat + end.lat) / 2 - lat0;
  return east * Math.sin(azimuth) + north * Math.cos(azimuth) > 0
    ? 'eave'
    : 'ridge';
};

const mountingFactLabel = (
  hasRoofPlane: boolean,
  mounting: { onFrames: boolean; pitchDegrees: number }
) => {
  if (!hasRoofPlane) return 'Confirm roof angles first';
  return mounting.onFrames
    ? `Tilt-up ${formatRoofPitch(mounting.pitchDegrees)}`
    : 'Flush';
};

/** A fact whose value can be overridden: "Override" reveals the input, pre-filled. */
const OverrideFact: FC<{
  label: string;
  value: ReactNode;
  open: boolean;
  onOpen?: () => void;
  /** "Use computed": back to the value the roof model or rules give. */
  onReset?: () => void;
  children?: ReactNode;
}> = (props) => (
  <div>
    <div className="flex min-h-8 items-center justify-between gap-3 py-1">
      <Text as="span" level="supporting">
        {props.label}
      </Text>
      <div className="flex min-w-0 items-center gap-1 text-right">
        <Text as="span">{props.value}</Text>
        {props.onOpen && !props.open && (
          <Button
            variant="quaternary"
            size="sm"
            className="min-h-11 px-2 sm:min-h-0"
            aria-label={`Override ${props.label.toLowerCase()}`}
            onClick={props.onOpen}
          >
            Override
          </Button>
        )}
        {props.onReset && (
          <Button
            variant="quaternary"
            size="sm"
            className="min-h-11 px-2 sm:min-h-0"
            aria-label={`Use computed ${props.label.toLowerCase()}`}
            onClick={props.onReset}
          >
            Use computed
          </Button>
        )}
      </div>
    </div>
    {props.open && <div className="pb-2">{props.children}</div>}
  </div>
);

const RoofControls: FC<
  ContextPanelProps & { selectedFace: WorkingFace; roofNumber: number }
> = (props) => {
  const {
    selectedFace,
    roofNumber,
    selectedFaceExcluded = false,
    workingPanels,
    faceSummaries,
    onSetSelectedFacePitch,
    onSetFacePanelCount,
    savingsRegion,
    placementRules,
    snapToArray = false,
    onSnapToArrayChange,
    onAutoRepack,
  } = props;
  const roof = physicalRoofPlane(selectedFace);
  const anglesUnknown = !hasMeasuredPlane(selectedFace);
  const rules = parseSolarPlacementRules(placementRules);
  const numberFormat = usePreferredNumberFormat({ maximumFractionDigits: 0 });
  const facePanels = workingPanels.filter(
    (panel) => panel.faceId === selectedFace.id
  );
  const facePanelsCount = facePanels.filter((panel) => panel.included).length;
  let explicitFrames: boolean | undefined;
  if (selectedFace.mounting?.mode === 'tilt') explicitFrames = true;
  if (selectedFace.mounting?.mode === 'flush') explicitFrames = false;
  const skipReason = selectedFace.roofPlane
    ? packSkipReason({
        pitchDegrees: roof.pitchDegrees,
        azimuthDegrees: roof.azimuthDegrees,
        onFrames: explicitFrames,
        sunAccessPercent: selectedFace.shading?.annualSunAccessPercent,
        rules,
      })
    : undefined;
  const summary = faceSummaries.find((face) => face.faceId === selectedFace.id);
  const mounting = resolveSolarMounting(roof, selectedFace.mounting, rules);
  const [overrides, setOverrides] = useState({
    pitch: false,
    bearing: false,
    mounting: false,
  });
  const openOverride = (key: keyof typeof overrides) =>
    setOverrides((current) => ({ ...current, [key]: true }));
  const closeOverride = (key: keyof typeof overrides) =>
    setOverrides((current) => ({ ...current, [key]: false }));
  const computed = props.computedRoofPlane;
  const pitchOverridden =
    !!computed && Math.abs(computed.pitchDegrees - roof.pitchDegrees) >= 0.05;
  const bearingOverridden =
    !!computed &&
    Math.abs(computed.azimuthDegrees - roof.azimuthDegrees) >= 0.05;
  /** A pitch or bearing change, like mounting, repacks the roof. */
  const setRoofPlane = (next: SolarRoofPlane) => {
    if (!selectedFace.roofPlane && props.onConfirmSelectedRoofPlane)
      props.onConfirmSelectedRoofPlane(next);
    else if (next.azimuthDegrees !== roof.azimuthDegrees)
      props.onConfirmSelectedRoofPlane?.(next);
    else onSetSelectedFacePitch(next.pitchDegrees);
    onAutoRepack?.('selected');
  };
  const setMounting = (next: SolarMounting) => {
    props.onSetSelectedFaceMounting?.(next);
    onAutoRepack?.('selected');
  };

  const methods = useForm<FaceFormValues>({
    resolver: zodResolver(FACE_FORM_SCHEMA),
    values: {
      roofBearing: anglesUnknown
        ? null
        : Math.round(roof.azimuthDegrees * 10) / 10,
      mounting: selectedFace.mounting?.mode ?? 'auto',
      panelTilt:
        selectedFace.mounting?.mode === 'tilt'
          ? selectedFace.mounting.panelTiltDegrees
          : (placementRules?.flatRoof.frameTiltDeg ?? 15),
      pitch: anglesUnknown ? null : roof.pitchDegrees,
      anchorOffset:
        (selectedFace.roofEdgeAnchor?.offsetM ?? 0) *
        (savingsRegion === 'us' ? 1 / 0.0254 : 1000),
    },
  });
  const enteredPitch = methods.watch('pitch');
  const enteredBearing = methods.watch('roofBearing');
  const enteredRoofPlane =
    enteredPitch != null && enteredBearing != null
      ? { pitchDegrees: enteredPitch, azimuthDegrees: enteredBearing }
      : null;
  const mountingRef = useRef<HTMLDivElement>(null);
  const referenceRef = useRef<HTMLDivElement>(null);
  const { focusRequest, onFocusHandled } = props;
  useEffect(() => {
    const destination = focusRequest?.destination;
    let target: HTMLDivElement | null = null;
    if (destination === 'mounting') target = mountingRef.current;
    // Rotation lives on the canvas; its old inspector home was beside Reference.
    if (destination === 'reference' || destination === 'alignment')
      target = referenceRef.current;
    target?.scrollIntoView?.({ block: 'start' });
    target?.focus({ preventScroll: true });
    if (target) onFocusHandled?.();
  }, [focusRequest, onFocusHandled]);
  const accessLabel = savingsRegion === 'us' ? 'Solar access' : 'Sun access';
  const lengthUnit = savingsRegion === 'us' ? 'in' : 'mm';
  const emptyPanelMessage = !selectedFace.roofPlane
    ? 'Confirm roof pitch and bearing before placing panels.'
    : 'No panels fit this roof. Adjust its outline or choose a smaller panel.';
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <FactRows
          label={`Roof ${roofNumber} facts`}
          facts={[
            {
              label: anglesUnknown ? 'Plan area' : 'Roof area',
              value: formatRoofArea(
                roofSurfaceAreaMeters2(
                  selectedFace.areaMeters2,
                  roof.pitchDegrees
                ),
                savingsRegion
              ),
            },
            summary?.annualKwh != null && {
              label: 'Generation',
              value: `${numberFormat.format(summary.annualKwh)} kWh/yr`,
            },
          ]}
        />
      </div>
      <Form
        methods={methods}
        onSubmit={() => undefined}
        className="space-y-4 [&_input:not([type=range])]:bg-gray-50"
      >
        <Divider />
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <H4>Panels</H4>
            <div className="flex items-center gap-2">
              <IncrementableNumberInput
                aria-label="Number of panels on this roof"
                value={facePanelsCount}
                min={0}
                max={facePanels.length}
                sizeVariant="sm"
                wrapperClassName="w-32 shrink-0"
                disabled={
                  props.isArrayRotating ||
                  selectedFaceExcluded ||
                  !facePanels.length
                }
                onChange={(count) => {
                  if (
                    count == null ||
                    !Number.isInteger(count) ||
                    count < 0 ||
                    count > facePanels.length ||
                    count === facePanelsCount
                  )
                    return;
                  onSetFacePanelCount(selectedFace.id, count);
                }}
              />
              <Text as="span">/ {facePanels.length}</Text>
            </div>
          </div>
          {selectedFaceExcluded && (
            <div className="flex items-center justify-between gap-2">
              <Text level="supporting">Excluded</Text>
              {props.onToggleSelectedFaceIncluded && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={props.isArrayRotating}
                  onClick={props.onToggleSelectedFaceIncluded}
                >
                  Include
                </Button>
              )}
            </div>
          )}
          {selectedFace.rulesOverride && (
            <div className="flex items-center justify-between gap-2">
              <Text level="supporting">Array rules overridden</Text>
              {props.onFollowRulesSelectedFace && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={props.isArrayRotating}
                  onClick={props.onFollowRulesSelectedFace}
                >
                  Follow rules
                </Button>
              )}
            </div>
          )}
          {!selectedFaceExcluded &&
            !facePanels.length &&
            (skipReason && skipReason !== 'too-steep' ? (
              <div className="flex items-center justify-between gap-2">
                <Text level="supporting">Excluded by Array rules</Text>
                {props.onIncludeSelectedFaceAnyway && (
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={props.isArrayRotating}
                    onClick={props.onIncludeSelectedFaceAnyway}
                  >
                    Include anyway
                  </Button>
                )}
              </div>
            ) : (
              <Text level="supporting">
                {skipReason === 'too-steep'
                  ? 'Too steep for panels'
                  : emptyPanelMessage}
              </Text>
            ))}
          {onSnapToArrayChange && (
            <div className="flex items-center justify-between gap-3">
              <Text as="span">Snap to array</Text>
              <Switch
                aria-label="Snap to array"
                sizeVariant={SwitchSizeVariant.SM}
                value={snapToArray}
                onChange={onSnapToArrayChange}
              />
            </div>
          )}
        </div>
        <Divider />
        <div
          ref={mountingRef}
          tabIndex={-1}
          aria-label="Mounting settings"
          className="space-y-1"
        >
          <H4>Pitch and mounting</H4>
          {selectedFace.planeFit?.status === 'review' && (
            <Text level="supporting">
              Angles need review. Check the estimated pitch and bearing against
              your survey.
              {selectedFace.planeFit.note && ` ${selectedFace.planeFit.note}`}
            </Text>
          )}
          {anglesUnknown && props.onConfirmSelectedRoofPlane && (
            <div className="space-y-2">
              <Text>
                Roof angles are unknown. Enter and confirm pitch and bearing
                before placing panels.
                {selectedFace.planeFit?.note &&
                  ` ${selectedFace.planeFit.note}`}
              </Text>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={props.isArrayRotating || !enteredRoofPlane}
                onClick={() =>
                  enteredRoofPlane && setRoofPlane(enteredRoofPlane)
                }
              >
                Confirm entered pitch and bearing
              </Button>
            </div>
          )}
          <OverrideFact
            label="Pitch"
            value={
              <>
                {anglesUnknown ? 'Unknown' : formatRoofPitch(roof.pitchDegrees)}
                {selectedFace.pitchSource === 'assumed' && !anglesUnknown && (
                  <span className="ml-1 text-sm text-gray-500">Est.</span>
                )}
              </>
            }
            open={anglesUnknown || overrides.pitch}
            onOpen={() => openOverride('pitch')}
            onReset={
              computed && (overrides.pitch || pitchOverridden)
                ? () => {
                    closeOverride('pitch');
                    if (pitchOverridden)
                      setRoofPlane({
                        pitchDegrees: computed.pitchDegrees,
                        azimuthDegrees: roof.azimuthDegrees,
                      });
                  }
                : undefined
            }
          >
            <Controller
              control={methods.control}
              name="pitch"
              render={({ field: { name, onChange, ...field } }) => (
                <Field name={name}>
                  <div className="flex items-center justify-between gap-2">
                    <Field.Label>Roof pitch</Field.Label>
                    <InputGroup sizeVariant="sm" className="w-28 shrink-0">
                      <NumberInput
                        {...field}
                        as={InputGroup.RawInput}
                        inputClassName="min-h-11 px-2"
                        min={0}
                        max={75}
                        disabled={props.isArrayRotating}
                        step={0.1}
                        format={{ maximumFractionDigits: 1 }}
                        onBlur={() => {
                          field.onBlur();
                          if (field.value == null && !anglesUnknown)
                            onChange(roof.pitchDegrees);
                        }}
                        onChange={(pitch) => {
                          // Committing the 1 dp display is not an edit.
                          if (
                            pitch != null &&
                            field.value != null &&
                            pitch !== field.value &&
                            Math.round(field.value * 10) / 10 === pitch
                          )
                            return;
                          onChange(pitch);
                          if (anglesUnknown) return;
                          if (
                            pitch == null ||
                            !Number.isFinite(pitch) ||
                            pitch < 0 ||
                            pitch > 75
                          )
                            return;
                          setRoofPlane({
                            pitchDegrees: pitch,
                            azimuthDegrees: roof.azimuthDegrees,
                          });
                        }}
                      />
                      <InputGroup.Text>°</InputGroup.Text>
                    </InputGroup>
                  </div>
                </Field>
              )}
            />
          </OverrideFact>
          <OverrideFact
            label="Bearing"
            value={
              anglesUnknown
                ? 'Unknown'
                : `${Math.round(roof.azimuthDegrees * 10) / 10}° (${compassShort(roof.azimuthDegrees)})`
            }
            open={anglesUnknown || overrides.bearing}
            onOpen={
              props.onConfirmSelectedRoofPlane
                ? () => openOverride('bearing')
                : undefined
            }
            onReset={
              computed && (overrides.bearing || bearingOverridden)
                ? () => {
                    closeOverride('bearing');
                    if (bearingOverridden)
                      setRoofPlane({
                        pitchDegrees: roof.pitchDegrees,
                        azimuthDegrees: computed.azimuthDegrees,
                      });
                  }
                : undefined
            }
          >
            <Controller
              control={methods.control}
              name="roofBearing"
              render={({ field: { name, onChange, ...field } }) => (
                <Field name={name}>
                  <div className="flex items-center justify-between gap-2">
                    <Field.Label>Roof bearing</Field.Label>
                    <InputGroup sizeVariant="sm" className="w-28 shrink-0">
                      <NumberInput
                        {...field}
                        as={InputGroup.RawInput}
                        inputClassName="min-h-11 px-2"
                        min={0}
                        max={360}
                        step={0.1}
                        format={{ maximumFractionDigits: 1 }}
                        disabled={props.isArrayRotating}
                        onChange={(bearing) => {
                          onChange(bearing);
                          if (anglesUnknown) return;
                          if (
                            bearing == null ||
                            !Number.isFinite(bearing) ||
                            bearing < 0 ||
                            bearing > 360
                          )
                            return;
                          setRoofPlane({
                            pitchDegrees: roof.pitchDegrees,
                            azimuthDegrees: bearing,
                          });
                        }}
                      />
                      <InputGroup.Text>°</InputGroup.Text>
                    </InputGroup>
                  </div>
                </Field>
              )}
            />
          </OverrideFact>
          <OverrideFact
            label="Mounting"
            value={mountingFactLabel(!!selectedFace.roofPlane, mounting)}
            open={overrides.mounting}
            // An older design without a recorded roof plane shows its
            // historical panel angles: override pitch or bearing first, so
            // the plane is never inferred from panel tilt.
            onOpen={
              props.onSetSelectedFaceMounting && selectedFace.roofPlane
                ? () => openOverride('mounting')
                : undefined
            }
            onReset={
              props.onSetSelectedFaceMounting &&
              (overrides.mounting ||
                (selectedFace.mounting &&
                  selectedFace.mounting.mode !== 'auto'))
                ? () => {
                    closeOverride('mounting');
                    if (selectedFace.mounting?.mode !== 'auto')
                      setMounting({ mode: 'auto' });
                  }
                : undefined
            }
          >
            <div className="space-y-2">
              <Controller
                control={methods.control}
                name="mounting"
                render={({ field: { name, value, onChange } }) => (
                  <Field name={name}>
                    <div className="[&>[role=radiogroup]]:flex-nowrap [&_[role=radio]]:min-h-11 [&_[role=radio]]:min-w-0 [&_[role=radio]]:flex-1 [&_[role=radio]]:px-2">
                      <ButtonRadioGroup
                        aria-label="Panel mounting"
                        sizeVariant="sm"
                        value={value}
                        disabled={props.isArrayRotating}
                        options={[
                          { label: 'Automatic', value: 'auto' },
                          { label: 'Flush-mounted', value: 'flush' },
                          { label: 'Tilt-up', value: 'tilt' },
                        ]}
                        CustomOption={({ option }) => (
                          <>
                            <span aria-hidden>
                              {
                                (
                                  {
                                    auto: 'Auto',
                                    flush: 'Flush',
                                    tilt: 'Tilt-up',
                                  } as Record<string, string>
                                )[option.value]
                              }
                            </span>
                            <span className="sr-only">{option.label}</span>
                          </>
                        )}
                        onChange={(mode) => {
                          onChange(mode);
                          setMounting(
                            mode === 'tilt'
                              ? {
                                  mode,
                                  panelTiltDegrees:
                                    methods.getValues('panelTilt') ?? 15,
                                }
                              : { mode }
                          );
                        }}
                      />
                    </div>
                  </Field>
                )}
              />
              {selectedFace.mounting?.mode === 'tilt' && (
                <Controller
                  control={methods.control}
                  name="panelTilt"
                  render={({ field: { name, onChange, ...field } }) => (
                    <Field
                      name={name}
                      className="flex items-center justify-between gap-2"
                    >
                      <Field.Label>Panel tilt</Field.Label>
                      <InputGroup sizeVariant="sm" className="w-20 shrink-0">
                        <NumberInput
                          {...field}
                          as={InputGroup.RawInput}
                          aria-label="Panel tilt above horizontal"
                          inputClassName="min-h-11 px-2"
                          min={0}
                          max={45}
                          disabled={props.isArrayRotating}
                          onChange={(value) => {
                            onChange(value);
                            if (value != null && value >= 0 && value <= 45)
                              setMounting({
                                mode: 'tilt',
                                panelTiltDegrees: value,
                              });
                          }}
                        />
                        <InputGroup.Text>°</InputGroup.Text>
                      </InputGroup>
                    </Field>
                  )}
                />
              )}
            </div>
          </OverrideFact>
        </div>
        {props.onSetSelectedFaceAnchor && (
          <>
            <Divider />
            <div
              ref={referenceRef}
              tabIndex={-1}
              aria-label="Reference settings"
              className="space-y-2"
            >
              <div className="flex items-center justify-between gap-2">
                <H4>Reference edge</H4>
                <ButtonDropdown
                  variant="secondary"
                  size="sm"
                  aria-label="Choose array reference edge"
                  disabled={props.isArrayRotating}
                  dropdownOptions={{
                    items: [
                      {
                        label: 'Automatic (no reference edge)',
                        onClick: () => {
                          props.onSetSelectedFaceAnchor?.(null);
                        },
                      },
                      ...selectedFace.polygon.outer.map(
                        (point, index, ring) => {
                          const role = inferRoofEdgeRole(
                            ring,
                            index,
                            roof.azimuthDegrees
                          );
                          return {
                            label: `Edge ${index + 1} · ${role}`,
                            onClick: () => {
                              props.onSetSelectedFaceAnchor?.({
                                edgeIndex: index,
                                endpoints: [
                                  point,
                                  ring[(index + 1) % ring.length],
                                ],
                                role,
                                offsetM:
                                  selectedFace.roofEdgeAnchor?.offsetM ??
                                  rules.edgeSetbackM,
                              });
                            },
                          };
                        }
                      ),
                    ],
                  }}
                >
                  {selectedFace.roofEdgeAnchor
                    ? `Edge ${selectedFace.roofEdgeAnchor.edgeIndex + 1} · ${selectedFace.roofEdgeAnchor.role}`
                    : 'Automatic'}
                </ButtonDropdown>
              </div>
              {selectedFace.roofEdgeAnchor && (
                <Controller
                  control={methods.control}
                  name="anchorOffset"
                  render={({ field: { name, onChange, ...field } }) => (
                    <Field
                      name={name}
                      className="flex items-center justify-between gap-2"
                    >
                      <Field.Label>Distance ({lengthUnit})</Field.Label>
                      <NumberInput
                        {...field}
                        aria-label={`Edge to nearest module (${lengthUnit})`}
                        sizeVariant="sm"
                        className="w-20 shrink-0"
                        min={0}
                        max={savingsRegion === 'us' ? 100 / 0.0254 : 100000}
                        disabled={props.isArrayRotating}
                        step={savingsRegion === 'us' ? 0.1 : 1}
                        onChange={(value) => {
                          onChange(value);
                          const offsetM =
                            value == null
                              ? null
                              : value *
                                (savingsRegion === 'us' ? 0.0254 : 0.001);
                          if (
                            offsetM != null &&
                            offsetM >= 0 &&
                            offsetM <= 100
                          ) {
                            props.onSetSelectedFaceAnchor?.({
                              ...selectedFace.roofEdgeAnchor!,
                              offsetM,
                            });
                          }
                        }}
                      />
                    </Field>
                  )}
                />
              )}
            </div>
          </>
        )}
      </Form>
      {selectedFace.shading && (
        <>
          <Divider />
          <div className="space-y-3">
            <H4>
              {accessLabel} {selectedFace.shading.annualSunAccessPercent}%
            </H4>
            <SunAccessHeatmap shading={selectedFace.shading} />
          </div>
        </>
      )}
    </div>
  );
};

const RepositionPanel: FC<
  Pick<
    ContextPanelProps,
    | 'isGeneratingGeometry'
    | 'confirmReposition'
    | 'cancelTool'
    | 'parcelMismatchHint'
  >
> = (props) => {
  const {
    isGeneratingGeometry,
    confirmReposition,
    cancelTool,
    parcelMismatchHint,
  } = props;
  const [unmovedAsked, setUnmovedAsked] = useState(false);
  return (
    <div className="space-y-3">
      <H4>Reposition analysis pin</H4>
      <Text level="supporting">
        Drag the pin onto the right roof, then reanalyse.
      </Text>
      {parcelMismatchHint}
      {unmovedAsked && !isGeneratingGeometry && (
        <Text role="status">
          The pin has not moved. Choose Reanalyse anyway to refresh this roof.
        </Text>
      )}
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            const outcome = confirmReposition(
              unmovedAsked ? { force: true } : undefined
            );
            setUnmovedAsked(outcome === 'unmoved');
          }}
          isProcessing={isGeneratingGeometry}
          disabled={isGeneratingGeometry}
        >
          {unmovedAsked ? 'Reanalyse anyway' : 'Reanalyse roof'}
        </Button>
        <Button
          variant="quaternary"
          size="sm"
          onClick={cancelTool}
          disabled={isGeneratingGeometry}
        >
          Done
        </Button>
      </div>
      {isGeneratingGeometry && (
        <Text role="status" aria-live="polite" level="supporting">
          Analysing roof imagery · you can leave and come back
        </Text>
      )}
    </div>
  );
};

const ContextPanelBody: FC<
  ContextPanelProps & { orderedFaceSummaries: FaceSummary[] }
> = (props) => {
  const {
    tool,
    selectedFace,
    selectedObstacleIndex,
    cancelTool,
    clearSelection,
    onDeleteSelectedObstacle,
    orderedFaceSummaries,
    onSelectFace,
    layoutDirty,
    onResetToAuto,
    workingObstacles,
    roofModelLabel,
    geometryEditedBadge,
    imageryQualityLabel,
    imageryMonth,
    isArrayRotating,
    savings,
    savingsRegion,
    carbonOffsetFactorKgPerMwh,
    netMeteringAllowed,
  } = props;
  const numberFormat = usePreferredNumberFormat({ maximumFractionDigits: 1 });
  const centsFormat = usePreferredNumberFormat({ maximumFractionDigits: 2 });
  const account = useAccount();
  const exportTariffMinorPerKwh = resolveSolarSavingsRates(savingsRegion, {
    exportTariffMinorPerKwh: account?.solarExportTariffMinorPerKwh,
  }).exportTariffMinorPerKwh;
  if (tool === 'pin') return <RepositionPanel {...props} />;
  if (tool === 'draw') return <DrawPanel {...props} />;
  if (tool === 'obstacle')
    return (
      <ToolHint title="Add obstacle" onCancel={cancelTool} cancelLabel="Cancel">
        Click two opposite corners of the obstruction.
      </ToolHint>
    );
  if (tool === 'move')
    return (
      <ToolHint title="Shift all roofs together" onCancel={cancelTool}>
        Drag the roofs into place.
      </ToolHint>
    );
  if (tool === 'trace')
    return (
      <ToolHint title="Trace building outline" onCancel={cancelTool}>
        Click a building outline.
      </ToolHint>
    );
  if (selectedFace)
    return (
      <RoofControls
        {...props}
        // Overrides open per roof: another roof starts with its facts.
        key={selectedFace.id}
        selectedFace={selectedFace}
        roofNumber={
          orderedFaceSummaries.findIndex(
            (face) => face.faceId === selectedFace.id
          ) + 1
        }
      />
    );
  if (selectedObstacleIndex != null)
    return (
      <div className="space-y-3">
        <ToolHint title="Obstacle" onCancel={clearSelection}>
          Panels avoid this area. Remove it if the skylight, vent or chimney is
          not present.
        </ToolHint>
        <Button
          variant="secondary"
          size="sm"
          onClick={onDeleteSelectedObstacle}
        >
          <UntitledIcon name="trash-01.3" className="size-4" />
          Remove obstacle
        </Button>
      </div>
    );
  return (
    <div className="space-y-4">
      <Text level="supporting">Select a roof on the map or in the list.</Text>
      {orderedFaceSummaries.length > 0 ? (
        <FaceList
          summaries={orderedFaceSummaries}
          onSelectFace={onSelectFace}
          savingsRegion={savingsRegion}
        />
      ) : (
        <Text level="supporting">
          No roof areas yet. Use Draw roof area to add one.
        </Text>
      )}
      {layoutDirty && (
        <Button variant="quaternary" size="sm" onClick={onResetToAuto}>
          <UntitledIcon name="refresh-cw-01" className="size-4" />
          Reset to automatic layout
        </Button>
      )}
      <Divider />
      <div className="space-y-2">
        <H4>Analysis</H4>
        <FactRows
          label="Analysis facts"
          facts={[
            {
              label: 'Roof model',
              value: `${roofModelLabel ?? 'Manually drawn roof model'}${geometryEditedBadge ? ' (edited)' : ''}`,
            },
            (!!imageryMonth || !!imageryQualityLabel) && {
              label: 'Imagery',
              value: [imageryMonth, imageryQualityLabel?.label.toLowerCase()]
                .filter(Boolean)
                .join(' · '),
            },
            { label: 'Obstacles', value: String(workingObstacles) },
            carbonOffsetFactorKgPerMwh != null &&
              savings.annualKwh != null &&
              savings.annualKwh > 0 && {
                label: 'CO₂ offset',
                value: `${numberFormat.format(
                  (savings.annualKwh / 1000) *
                    (carbonOffsetFactorKgPerMwh / 1000)
                )} t/yr`,
              },
            savingsRegion === 'us' &&
              netMeteringAllowed != null && {
                label: 'Net metering',
                value: `${netMeteringAllowed ? 'Offered' : 'Not offered'} · export ${centsFormat.format(exportTariffMinorPerKwh)}¢/kWh`,
              },
          ]}
        />
      </div>
    </div>
  );
};

/** The Roof tab body: changes with the active tool and the selection. */
const ContextPanel: FC<ContextPanelProps> = (props) => {
  const orderedFaceSummaries = props.faceSummaries;
  const [rulesOpen, toggleRules] = useInspectorDialog('#solar-array-rules');
  const rulesEnabled = !!(props.placementRules && props.onPlacementRulesChange);
  const selectedRoofNumber = props.selectedFace
    ? orderedFaceSummaries.findIndex(
        (face) => face.faceId === props.selectedFace?.id
      ) + 1
    : 0;
  const heading = props.selectedFace
    ? `Roof ${selectedRoofNumber} · ${roofBearingLabel({ azimuthDegrees: physicalRoofPlane(props.selectedFace).azimuthDegrees, planeFit: props.selectedFace.planeFit })}`
    : 'All roofs';
  // Tool and obstacle panels carry their own title.
  const showHeader =
    props.tool === 'select' &&
    props.selectedObstacleIndex == null &&
    (rulesEnabled || !!props.selectedFace);
  return (
    <div className="space-y-5">
      {props.isArrayRotating && (
        <Text level="supporting" role="status">
          Apply or cancel the rotation to edit the layout
        </Text>
      )}
      {showHeader && (
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <H3>{heading}</H3>
          </div>
          {rulesEnabled && (
            <Button
              variant="secondary"
              size="sm"
              className="shrink-0"
              onClick={toggleRules}
              disabled={props.isArrayRotating}
            >
              Array rules (all roofs)
            </Button>
          )}
        </div>
      )}
      {props.placementRules && props.onPlacementRulesChange && (
        <Modal
          title="Array rules"
          size="sm"
          isOpen={rulesOpen}
          onClose={toggleRules}
          disableBackdropClick={false}
        >
          <Modal.Body className="space-y-4">
            <fieldset disabled={props.isArrayRotating}>
              <PlacementRulesFields
                variant="designer"
                value={parseSolarPlacementRules(props.placementRules)}
                region={props.savingsRegion}
                onChange={(next) => {
                  props.onPlacementRulesChange?.(next);
                  props.onAutoRepack?.('all');
                }}
              />
            </fieldset>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button variant="secondary" onClick={toggleRules}>
                Done
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Modal>
      )}
      <ContextPanelBody
        {...props}
        orderedFaceSummaries={orderedFaceSummaries}
      />
    </div>
  );
};

export default ContextPanel;
